import { ComponentIds, Entity, HierarchyUtils, PartPair } from '@inter-ikea-core/core';
import { CoreApi, OrganizerComponent, Product, ProductType } from '@inter-ikea-storageone/range';
import {
  addFrontLayoutToFrame,
  getFrontsInScene,
  getFrontsOnFrame,
  getMajorityDesign
} from 'rangeConfig/shared/utils/frontLayouts';
import { addInteriorSuggestionEntityToFrame } from 'rangeConfig/shared/utils/interiorSuggestions';

export const createPlatsaInteriorSuggestionEntity = ({
  selectedEntity,
  coreApi,
  interiorSuggestionProduct,
  products
}: {
  selectedEntity: Entity;
  coreApi: CoreApi;
  interiorSuggestionProduct: Product;
  products: Product[];
}) => {
  if (interiorSuggestionProduct.type !== ProductType.PLATSA_INTERIOR_SUGGESTION) {
    return coreApi
      .getServices()
      .productManager.createEntity(interiorSuggestionProduct.coreProductId);
  }

  const frontEntitesOnFrame = getFrontsOnFrame(selectedEntity);
  const frontEntitesInScene = getFrontsInScene(coreApi);
  const frontDesign =
    frontEntitesOnFrame.length > 0
      ? getMajorityDesign(frontEntitesOnFrame)
      : getMajorityDesign(frontEntitesInScene);

  const productDTO = coreApi
    .getInteriorSuggestionProductCatalog()
    ?.findProductDTO(interiorSuggestionProduct.coreProductId);

  let interiorSuggestionEntity;

  // For PLATSA we need to change drawers on interior suggestion to majority front in frame/scene
  if (frontDesign && productDTO && productDTO.template.parts) {
    const replaceParts = productDTO.template.parts
      .map(part => {
        const partProduct = products.find(p => p.coreProductId === part.id);
        if (!partProduct) return undefined;

        let product;
        if (partProduct.type === ProductType.PLATSA_DRAWER_ASM) {
          product = products.find(
            p =>
              p.communicatedDimensions.depth === partProduct.communicatedDimensions.depth &&
              p.communicatedDimensions.width === partProduct.communicatedDimensions.width &&
              p.type === partProduct.type &&
              p.design === frontDesign?.design
          );
        }
        return product ? new PartPair(part.partKey, product.coreProductId) : undefined;
      })
      .filter((p): p is PartPair => p !== undefined);
    interiorSuggestionEntity = coreApi
      .getInteriorSuggestionProductCatalog()
      ?.createCustomAssembly(
        interiorSuggestionProduct.coreProductId,
        replaceParts,
        coreApi.getServices().productManager
      );
  } else {
    interiorSuggestionEntity = coreApi
      .getServices()
      .productManager.createEntity(interiorSuggestionProduct.coreProductId);
  }
  return interiorSuggestionEntity;
};

export const addInteriorsSuggestionAndFront = (
  interiorSuggestionEntity: Entity,
  coreApi: CoreApi,
  frontLayoutProduct: Product | undefined,
  selectedEntity: Entity,
  products: Product[],
  design: { name: string; design: string } | null
) => {
  addInteriorSuggestionEntityToFrame(interiorSuggestionEntity, selectedEntity, coreApi);

  // In markets where the default front layout is not sold we cannot add that layout to the frame which means we will skip it instead
  if (frontLayoutProduct) {
    const newPlacement = addFrontLayoutToFrame(
      frontLayoutProduct,
      selectedEntity,
      products,
      coreApi,
      design
    );

    if (newPlacement && !newPlacement?.valid) {
      coreApi.createConflict(newPlacement);
    }
  }
};

export const addInteriorSuggestionToPlatsaFrame = (
  interiorSuggestionProductId: string,
  products: Product[],
  menu: string,
  coreApi: CoreApi
) => {
  const interiorSuggestionProduct = products.find(
    product => product.coreProductId === interiorSuggestionProductId
  );

  if (!interiorSuggestionProduct) return;

  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return;

  const frontEntitesOnFrame = getFrontsOnFrame(selectedEntity);
  const frontEntitesInScene = getFrontsInScene(coreApi);

  const previousFrontDesign =
    frontEntitesOnFrame.length > 0
      ? getMajorityDesign(frontEntitesOnFrame)
      : getMajorityDesign(frontEntitesInScene);

  // We need to make sure that we have a default front layout for the interior suggestion we try to add
  const defaultLayout = interiorSuggestionProduct.meta?.defaultFrontLayout;

  const frontLayoutProduct = products.find(product => {
    return product.coreProductId === defaultLayout;
  });

  // Get all previous doors and drawers in the scene
  const previousDoors = HierarchyUtils.children(selectedEntity).filter(c =>
      c.hasComponent(ComponentIds.DOOR_COMPONENT_ID)
    ),
    previousDrawers = HierarchyUtils.children(selectedEntity).filter(c =>
      c.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
    );
  const previousOrganizers = HierarchyUtils.children(selectedEntity).filter(c =>
    c.hasComponent(OrganizerComponent.id)
  );

  coreApi.history.historyAction(() => {
    const interiorSuggestionEntity = createPlatsaInteriorSuggestionEntity({
      selectedEntity,
      coreApi,
      interiorSuggestionProduct,
      products
    });

    if (!interiorSuggestionEntity) return;

    // If we have doors in the scene we might want to delete them before adding the new interior suggestion.
    // If we have drawers combined with doors we need to remove both the old doors and drawers and replacing them with a new front layout.
    // If we only have doors we first try to add the new interior suggestion, if the new interior suggestion contains drawers we need to remove the
    // doors before adding the new interior suggestion and use the default layout instead. If the new interior suggestion does not contain any drawers
    // we will first try to place the interior suggestion without changing the doors, but if we get a placement result we fall back to use the default front layout
    // for the interior suggestion.

    if (previousOrganizers.length > 0) {
      coreApi.entities.deleteMultiple(previousOrganizers);
    }
    if (previousDoors.length > 0) {
      // If we have drawers remove all doors and drawers
      if (previousDrawers.length > 0) {
        coreApi.entities.deleteMultiple([...previousDoors, ...previousDrawers]);

        addInteriorsSuggestionAndFront(
          interiorSuggestionEntity,
          coreApi,
          frontLayoutProduct,
          selectedEntity,
          products,
          previousFrontDesign
        );
      } else {
        // This determines if the new interior suggestion contains any drawers
        const productDTO =
          frontLayoutProduct &&
          coreApi.getFrontLayoutProductCatalog()?.findProductDTO(frontLayoutProduct.coreProductId);

        if (productDTO && productDTO.template.parts) {
          const drawerInNewFrontLayout = productDTO.template.parts.find(p =>
            p.partKey.includes(ProductType.PLATSA_DRAWER_ASM)
          );

          // If the interior suggestion contains drawers
          if (drawerInNewFrontLayout) {
            coreApi.entities.deleteMultiple(previousDoors);

            addInteriorsSuggestionAndFront(
              interiorSuggestionEntity,
              coreApi,
              frontLayoutProduct,
              selectedEntity,
              products,
              previousFrontDesign
            );

            // Otherwise just try to add the interior without changing doors
          } else {
            const placementResult = addInteriorSuggestionEntityToFrame(
              interiorSuggestionEntity,
              selectedEntity,
              coreApi
            );

            // If the interiors would collide with the doors we will just remove the old doors and replace them with the default front layout from
            // the interior suggestion. All of this is configured in the range
            if (!placementResult.valid) {
              coreApi.entities.deleteMultiple(previousDoors);
              addInteriorsSuggestionAndFront(
                interiorSuggestionEntity,
                coreApi,
                frontLayoutProduct,
                selectedEntity,
                products,
                previousFrontDesign
              );
            }
          }
        }
      }
      // If we don't have any doors at all we can just add the interior suggestion to the frame and hopefully everything works...
    } else {
      addInteriorSuggestionEntityToFrame(interiorSuggestionEntity, selectedEntity, coreApi);
    }
  });
};
